/**
 * marketing-hook-generator.ts
 * Auto-generates 3 outreach hook templates per marketing event.
 * Mi6 MAP system — Sales Hook Workflow.
 */

export interface MarketingEvent {
  id: string;
  name: string;
  activityType: string;
  startDate: string;
  endDate?: string;
  location?: string;
  cost?: number;
  targetAudience?: string;
  status: "Planned" | "In Progress" | "Completed" | "Cancelled";
  actual?: {
    costActual?: number;
    totalAttendees?: number;
    registeredWithUs?: number;
    boothStops?: number;
    mqlsAdded?: number;
    suspectsQualified?: number;
    prospectsQualified?: number;
    immediateOpportunities?: number;
    followUpMeetingsBooked?: number;
    notes?: string;
  };
  hooks?: {
    invite: HookEntry;
    participation: HookEntry;
    round_table: HookEntry;
  };
}

export interface HookEntry {
  text: string;
  approved: boolean;
  approved_at?: string;
}

export interface GeneratedHooks {
  invite: HookEntry;
  participation: HookEntry;
  round_table: HookEntry;
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return "{date}";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

export function generateHooks(event: MarketingEvent): GeneratedHooks {
  const name = event.name || "{event_name}";
  const date = formatDateShort(event.startDate);
  const location = event.location || "{location}";
  const audience = event.targetAudience || "{audience_description}";

  // Determine action verb based on activity type
  const actionVerb = (() => {
    const t = event.activityType;
    if (t === "Trade Shows") return "exhibiting at";
    if (t === "Sponsored Content") return "sponsoring";
    if (t === "Speaking Engagements (team as speakers)") return "presenting at";
    if (t === "Hosted Round Tables" || t === "In-Person Hosted Events") return "hosting";
    if (t === "Webinars / Web Events") return "running a session at";
    return "attending";
  })();

  const inviteText = `Hi [Prospect Name], Mi6 Group is ${actionVerb} **${name}** on **${date}**${location !== "{location}" ? ` in **${location}**` : ""}. Would you be open to meeting up while you're there? Even 20 minutes would be valuable to us.`;

  const participationText = `Hi [Prospect Name], we're putting together a panel at **${name}** on **{topic_placeholder}** — would you be interested in participating as a panelist? It would give you visibility with **${audience}**, and frankly your perspective would carry weight in that room.`;

  const roundTableText = `Hi [Prospect Name], we're hosting a small executive round table at **${name}** for **${audience}** to discuss **{topic_placeholder}**. We're keeping it to ~12 people. Want to be on the list?`;

  return {
    invite: { text: inviteText, approved: false },
    participation: { text: participationText, approved: false },
    round_table: { text: roundTableText, approved: false },
  };
}

// ─── LocalStorage helpers ─────────────────────────────────────────────────────

const EVENTS_KEY = "aip:marketing-events:v1";
const PLAN_KEY = "aip:marketing-plan:v1";
const HOOK_USAGE_KEY = "aip:hook-usage:v1";

export function loadEvents(): MarketingEvent[] {
  try {
    const raw = localStorage.getItem(EVENTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveEvents(events: MarketingEvent[]): void {
  localStorage.setItem(EVENTS_KEY, JSON.stringify(events));
}

export function seedEventsIfEmpty(): MarketingEvent[] {
  const existing = loadEvents();
  if (existing.length > 0) return existing;

  const seed: MarketingEvent[] = [
    {
      id: "evt-2026-01",
      name: "Profit+ Industrial CFO email drip",
      activityType: "Email Campaigns",
      startDate: "2026-05-05",
      endDate: "2026-06-26",
      cost: 2800,
      targetAudience: "CFOs and VP Finance at mid-market industrial manufacturers ($50M-$500M revenue). Top-10 ABM list weighted.",
      status: "In Progress",
    },
    {
      id: "evt-2026-02",
      name: "Toyota CTO webinar",
      activityType: "Webinars / Web Events",
      startDate: "2026-06-12",
      location: "Virtual",
      cost: 4200,
      targetAudience: "Tier 1 automotive R&D and engineering leadership. Toyota ABM track and adjacent OEM contacts.",
      status: "Planned",
    },
    {
      id: "evt-2026-03",
      name: "Stellantis Purchasing breakfast",
      activityType: "In-Person Hosted Events",
      startDate: "2026-07-08",
      location: "Auburn Hills, MI",
      cost: 6800,
      targetAudience: "Stellantis purchasing, supplier development, and program management. Top-10 ABM anchor account.",
      status: "Planned",
    },
    {
      id: "evt-2026-04",
      name: "AutomotiveTech.io trade show booth",
      activityType: "Trade Shows",
      startDate: "2026-09-18",
      endDate: "2026-09-20",
      location: "Novi, MI",
      cost: 22000,
      targetAudience: "Automotive Tier 1 and Tier 2 suppliers, plant operations leaders, manufacturing IT.",
      status: "Planned",
    },
    {
      id: "evt-2026-05",
      name: "Year-end MfgOps content series",
      activityType: "Sponsored Content",
      startDate: "2026-10-01",
      endDate: "2026-10-31",
      cost: 9500,
      targetAudience: "Operations VPs and Plant Directors across automotive, medical device, aerospace, and industrial equipment.",
      status: "Planned",
    },
    {
      id: "evt-2026-06",
      name: "LinkedIn paid pulse Q4",
      activityType: "Paid Ads / Digital Media",
      startDate: "2026-11-03",
      endDate: "2026-12-19",
      cost: 14000,
      targetAudience: "Manufacturing operations and finance leaders. Retargeting Top-10 ABM, look-alike industrial mid-market.",
      status: "Planned",
    },
  ];

  const withHooks = seed.map((ev) => ({ ...ev, hooks: generateHooks(ev) }));
  saveEvents(withHooks);
  return withHooks;
}

export interface HookUsageEntry {
  date: string;
  user: string;
  event_id: string;
  hook_type: "invite" | "participation" | "round_table";
}

export function loadHookUsage(): HookUsageEntry[] {
  try {
    const raw = localStorage.getItem(HOOK_USAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function logHookUsage(entry: HookUsageEntry): void {
  const usage = loadHookUsage();
  usage.push(entry);
  localStorage.setItem(HOOK_USAGE_KEY, JSON.stringify(usage));
}

export interface MarketingPlanRow {
  type: string;
  q1: number;
  q2: number;
  q3: number;
  q4: number;
  targetMqls: number;
  targetSqls: number;
  budget: number;
  mqlToSqlPct: number; // default 25
}

export interface MarketingPlan {
  rows: MarketingPlanRow[];
  savedAt: string;
}

export const ACTIVITY_TYPES = [
  "Trade Shows",
  "Hosted Round Tables",
  "Webinars / Web Events",
  "In-Person Hosted Events",
  "Email Campaigns",
  "Direct Mail Campaigns",
  "Podcast Appearances",
  "Sponsored Content",
  "Paid Ads / Digital Media",
  "Co-Marketing Partnerships",
  "Speaking Engagements (team as speakers)",
  "Other Outreach",
];

export function loadPlan(): MarketingPlan {
  try {
    const raw = localStorage.getItem(PLAN_KEY);
    return raw ? JSON.parse(raw) : getDefaultPlan();
  } catch {
    return getDefaultPlan();
  }
}

export function savePlan(plan: MarketingPlan): void {
  localStorage.setItem(PLAN_KEY, JSON.stringify(plan));
}

// 2026 MQL target per Bryan's specification: 600 annual (Q1 120 / Q2 150 / Q3 160 / Q4 170)
// Split across channels proportionally:
//   Trade Shows: 160 · Email Campaigns: 200 · Webinars: 120
//   Round Tables: 60 · Paid Ads/Digital: 60  (total = 600)
function getDefaultPlan(): MarketingPlan {
  return {
    rows: ACTIVITY_TYPES.map((type) => ({
      type,
      q1: type === "Trade Shows" ? 1 : type === "Email Campaigns" ? 3 : type === "Webinars / Web Events" ? 2 : 0,
      q2: type === "Trade Shows" ? 2 : type === "Email Campaigns" ? 4 : type === "Webinars / Web Events" ? 1 : type === "Hosted Round Tables" ? 1 : 0,
      q3: type === "Trade Shows" ? 1 : type === "Email Campaigns" ? 3 : type === "Webinars / Web Events" ? 2 : 0,
      q4: type === "Trade Shows" ? 2 : type === "Email Campaigns" ? 4 : type === "Speaking Engagements (team as speakers)" ? 1 : 0,
      // MQL targets scaled to 600 annual (from 90 default) per 2026 specification
      targetMqls:
        type === "Trade Shows"           ? 160 :
        type === "Email Campaigns"       ? 200 :
        type === "Webinars / Web Events" ? 120 :
        type === "Hosted Round Tables"   ? 60  :
        type === "Paid Ads / Digital Media" ? 60 : 0,
      targetSqls:
        type === "Trade Shows"           ? 40 :
        type === "Email Campaigns"       ? 50 :
        type === "Webinars / Web Events" ? 30 :
        type === "Hosted Round Tables"   ? 15 :
        type === "Paid Ads / Digital Media" ? 15 : 0,
      budget: type === "Trade Shows" ? 72000 : type === "Email Campaigns" ? 12000 : type === "Webinars / Web Events" ? 8000 : type === "Hosted Round Tables" ? 18000 : type === "Paid Ads / Digital Media" ? 36000 : 0,
      mqlToSqlPct: 25,
    })),
    savedAt: new Date().toISOString(),
  };
}
